import React, {useCallback, useState} from 'react';
import {Map} from 'immutable';
import {UserId} from 'records/User';
import {TeamId} from 'records/TeamId';
import Team from 'records/Team';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import GenericTooltip from 'components/pure/tooltips/GenericTooltip';
import Loading from 'components/pure/utils/Loading';

interface TeamCellProps {
  userId: UserId;
  teamId: TeamId;
  teamName: string;
  active: boolean;
  teams: Map<TeamId, Team>;
  onChange: (team: TeamId) => void;
}

export default function TeamCell({teamId, teams, onChange, teamName, active}: TeamCellProps) {
  const [loading, setLoading] = useState(false);
  const renderInput = useCallback(
    props => {
      if (loading) {
        return <Loading />;
      }

      if (!active) {
        return (
          <GenericTooltip
            positionTop
            tooltip="This user's team is inactive. Please select another.">
            <TextField
              {...props}
              variant="outlined"
              defaultValue={teamName}
              value={teamName}
              placeholder={teamName}
              error></TextField>
          </GenericTooltip>
        );
      }

      return <TextField {...props} variant="outlined" />;
    },
    [active, teamName, loading]
  );

  return (
    <Autocomplete
      size="small"
      renderInput={renderInput}
      options={teams.sortBy(t => t.name).toArray()}
      getOptionSelected={(a: Team, b: Team) => a.id === b.id}
      onChange={async (e, team) => {
        try {
          if (team) {
            setLoading(true);
            await onChange(team.id);
          }
        } catch {
          // Loading is set to false when component is remounted after search
          setLoading(false);
        }
      }}
      getOptionLabel={team => team.name}
      value={teams.get(teamId)}
      disableClearable
    />
  );
}
